import React, { useState, useMemo } from 'react'
import { 
  X, 
  User, 
  Wifi, 
  AlertCircle,
  Search,
  Plus,
  Trash2,
  Edit2,
  Save,
  Signal,
  Router
} from 'lucide-react'
import { PuertoNAP, EstadoPuerto, SplitterCompleto } from '../../types/nodes'
import { 
  asignarClienteAPuertoNAP, 
  liberarPuertoNAP,
  conectarPuertoAASplitter
} from '../../services/connectionService'

interface PuertosNAPConfigProps {
  napId: string
  nombreNAP?: string
  puertos: PuertoNAP[]
  splitters?: SplitterCompleto[]
  onUpdatePuertos: (puertos: PuertoNAP[]) => void
  onClose?: () => void
}

interface ClienteForm {
  clienteId: string
  nombreCliente: string
  ontId: string
  ontModelo: string
  paqueteServicio: string
}

const ESTADO_COLORS: Record<EstadoPuerto, { bg: string; text: string; label: string }> = {
  libre: { bg: 'bg-green-100', text: 'text-green-700', label: 'Libre' },
  ocupado: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Ocupado' },
  reservado: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Reservado' },
  dañado: { bg: 'bg-red-100', text: 'text-red-700', label: 'Dañado' }
}

const PAQUETES_SERVICIO = [
  '10 Mbps Básico',
  '30 Mbps Hogar',
  '50 Mbps Hogar Plus',
  '100 Mbps Premium',
  '200 Mbps Ultra',
  '500 Mbps Empresarial',
  '1 Gbps Empresarial Plus'
]

export const PuertosNAPConfig: React.FC<PuertosNAPConfigProps> = ({
  napId,
  nombreNAP,
  puertos,
  splitters = [],
  onUpdatePuertos,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterEstado, setFilterEstado] = useState<EstadoPuerto | 'todos'>('todos')
  const [editingPuerto, setEditingPuerto] = useState<number | null>(null)
  const [showClienteForm, setShowClienteForm] = useState<number | null>(null)
  const [clienteForm, setClienteForm] = useState<ClienteForm>({
    clienteId: '',
    nombreCliente: '',
    ontId: '',
    ontModelo: '',
    paqueteServicio: ''
  })
  const [error, setError] = useState<string | null>(null)

  const filteredPuertos = useMemo(() => {
    return puertos.filter(puerto => {
      const matchEstado = filterEstado === 'todos' || puerto.estado === filterEstado
      const matchSearch = searchTerm === '' || 
        puerto.puerto.toString().includes(searchTerm) ||
        puerto.cliente?.nombreCliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        puerto.cliente?.ontId?.toLowerCase().includes(searchTerm.toLowerCase())
      
      return matchEstado && matchSearch
    })
  }, [puertos, filterEstado, searchTerm])

  const estadisticas = useMemo(() => {
    return {
      total: puertos.length,
      libres: puertos.filter(p => p.estado === 'libre').length,
      ocupados: puertos.filter(p => p.estado === 'ocupado').length,
      reservados: puertos.filter(p => p.estado === 'reservado').length,
      dañados: puertos.filter(p => p.estado === 'dañado').length
    }
  }, [puertos])

  const handleAsignarCliente = (numeroPuerto: number) => {
    setError(null)
    
    if (!clienteForm.clienteId || !clienteForm.nombreCliente) {
      setError('El ID y nombre del cliente son obligatorios')
      return
    }
    
    const puertoActual = puertos.find(p => p.puerto === numeroPuerto)
    if (!puertoActual) {
      setError('Puerto no encontrado')
      return
    }
    
    const puertoActualizado = asignarClienteAPuertoNAP(puertoActual, {
      clienteId: clienteForm.clienteId,
      nombreCliente: clienteForm.nombreCliente,
      ontId: clienteForm.ontId || undefined,
      ontModelo: clienteForm.ontModelo || undefined,
      paqueteServicio: clienteForm.paqueteServicio || undefined
    })
    
    const nuevosPuertos = puertos.map(p => 
      p.puerto === numeroPuerto ? puertoActualizado : p
    )
    
    onUpdatePuertos(nuevosPuertos)
    setShowClienteForm(null)
    setClienteForm({
      clienteId: '',
      nombreCliente: '',
      ontId: '',
      ontModelo: '',
      paqueteServicio: ''
    })
  }

  const handleLiberarPuerto = (numeroPuerto: number) => {
    const puertoActual = puertos.find(p => p.puerto === numeroPuerto)
    if (!puertoActual) return
    
    if (!confirm(`¿Está seguro de liberar el puerto ${numeroPuerto}? Se desconectará al cliente ${puertoActual.cliente?.nombreCliente || 'desconocido'}.`)) {
      return
    }
    
    const puertoActualizado = liberarPuertoNAP(puertoActual)
    
    const nuevosPuertos = puertos.map(p => 
      p.puerto === numeroPuerto ? puertoActualizado : p
    )
    
    onUpdatePuertos(nuevosPuertos)
  }

  const handleCambiarEstado = (numeroPuerto: number, nuevoEstado: EstadoPuerto) => {
    const nuevosPuertos = puertos.map(p => 
      p.puerto === numeroPuerto ? { ...p, estado: nuevoEstado } : p
    )
    onUpdatePuertos(nuevosPuertos)
    setEditingPuerto(null)
  }

  const handleConectarASplitter = (
    numeroPuerto: number, 
    splitterId: string, 
    puertoSplitter: number
  ) => {
    const puertoActual = puertos.find(p => p.puerto === numeroPuerto)
    const splitter = splitters.find(s => s.id === splitterId)
    
    if (!puertoActual || !splitter) return
    
    const salida = splitter.salidas.find(s => s.numero === puertoSplitter)
    if (!salida) return
    
    const puertoActualizado = conectarPuertoAASplitter(
      puertoActual,
      splitterId,
      puertoSplitter,
      salida.colorPigtail,
      salida.atenuacion
    )
    
    const nuevosPuertos = puertos.map(p => 
      p.puerto === numeroPuerto ? puertoActualizado : p
    )
    
    onUpdatePuertos(nuevosPuertos)
  }

  const openClienteForm = (puerto: PuertoNAP) => {
    if (puerto.cliente) {
      setClienteForm({
        clienteId: puerto.cliente.clienteId,
        nombreCliente: puerto.cliente.nombreCliente,
        ontId: puerto.cliente.ontId || '',
        ontModelo: puerto.cliente.ontModelo || '',
        paqueteServicio: puerto.cliente.paqueteServicio || ''
      })
    } else {
      setClienteForm({
        clienteId: `CLI-${Date.now().toString(36).toUpperCase()}`,
        nombreCliente: '',
        ontId: '',
        ontModelo: '',
        paqueteServicio: ''
      })
    }
    setShowClienteForm(puerto.puerto)
  }

  const renderPuertoCard = (puerto: PuertoNAP) => {
    const estado = ESTADO_COLORS[puerto.estado]
    const isEditing = editingPuerto === puerto.puerto
    
    return (
      <div 
        key={puerto.puerto}
        className={`border rounded-lg p-3 ${
          puerto.estado === 'ocupado' ? 'bg-blue-50 border-blue-200' :
          puerto.estado === 'dañado' ? 'bg-red-50 border-red-200' :
          puerto.estado === 'reservado' ? 'bg-amber-50 border-amber-200' :
          'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-700">
              #{puerto.puerto}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${estado.bg} ${estado.text}`}>
              {estado.label}
            </span>
            <span className="text-xs text-gray-500">
              {puerto.tipoConector || 'SC/APC'}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setEditingPuerto(puerto.puerto)}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Editar estado"
                >
                  <Edit2 className="w-4 h-4 text-gray-500" />
                </button>
                {puerto.estado === 'ocupado' && (
                  <button
                    onClick={() => handleLiberarPuerto(puerto.puerto)}
                    className="p-1 hover:bg-red-100 rounded"
                    title="Liberar puerto"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                )}
                {puerto.estado === 'libre' && (
                  <button
                    onClick={() => openClienteForm(puerto)}
                    className="p-1 hover:bg-blue-100 rounded"
                    title="Asignar cliente"
                  >
                    <Plus className="w-4 h-4 text-blue-500" />
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => setEditingPuerto(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>
        
        {isEditing && (
          <div className="mb-3 flex gap-1">
            {(['libre', 'reservado', 'dañado'] as EstadoPuerto[]).map(e => (
              <button
                key={e}
                onClick={() => handleCambiarEstado(puerto.puerto, e)}
                className={`px-2 py-1 text-xs rounded ${
                  puerto.estado === e
                    ? `${ESTADO_COLORS[e].bg} ${ESTADO_COLORS[e].text}`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {ESTADO_COLORS[e].label}
              </button>
            ))}
          </div>
        )}
        
        {puerto.conexionSplitter && (
          <div className="mb-2 p-2 bg-purple-50 rounded text-xs">
            <div className="flex items-center gap-1 text-purple-700">
              <Signal className="w-3 h-3" />
              <span>Conectado a: {puerto.conexionSplitter.splitterId}</span>
            </div>
            <div className="text-purple-600 mt-1">
              Puerto SP: {puerto.conexionSplitter.puertoSplitter} • 
              {puerto.conexionSplitter.atenuacion?.toFixed(1)} dB
            </div>
          </div>
        )}
        
        {puerto.cliente && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium text-gray-800">
                {puerto.cliente.nombreCliente}
              </span>
            </div>
            
            {puerto.cliente.ontId && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Router className="w-4 h-4 text-gray-400" />
                <span>ONT: {puerto.cliente.ontId}</span>
              </div>
            )}
            
            {puerto.cliente.paqueteServicio && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Wifi className="w-4 h-4 text-gray-400" />
                <span>{puerto.cliente.paqueteServicio}</span>
              </div>
            )}
            
            <button
              onClick={() => openClienteForm(puerto)}
              className="text-xs text-blue-600 hover:underline"
            >
              Editar información
            </button>
          </div>
        )}
        
        {puerto.potencia !== undefined && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Potencia óptica:</span>
              <span className={`font-medium ${
                puerto.potencia > -25 ? 'text-green-600' :
                puerto.potencia > -28 ? 'text-amber-600' :
                'text-red-600'
              }`}>
                {puerto.potencia.toFixed(1)} dBm
              </span>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-lg max-h-[90vh] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Configuración de Puertos NAP
          </h2>
          <p className="text-sm text-gray-500">
            {nombreNAP || napId}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      <div className="p-4 border-b bg-gray-50">
        <div className="grid grid-cols-5 gap-2 mb-4">
          <div className="text-center p-2 bg-white rounded border">
            <p className="text-2xl font-bold text-gray-800">{estadisticas.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded border border-green-200">
            <p className="text-2xl font-bold text-green-600">{estadisticas.libres}</p>
            <p className="text-xs text-green-600">Libres</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
            <p className="text-2xl font-bold text-blue-600">{estadisticas.ocupados}</p>
            <p className="text-xs text-blue-600">Ocupados</p>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded border border-amber-200">
            <p className="text-2xl font-bold text-amber-600">{estadisticas.reservados}</p>
            <p className="text-xs text-amber-600">Reservados</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded border border-red-200">
            <p className="text-2xl font-bold text-red-600">{estadisticas.dañados}</p>
            <p className="text-xs text-red-600">Dañados</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por puerto, cliente u ONT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            />
          </div>
          
          <select
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value as any)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="todos">Todos los estados</option>
            <option value="libre">Libres</option>
            <option value="ocupado">Ocupados</option>
            <option value="reservado">Reservados</option>
            <option value="dañado">Dañados</option>
          </select>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {filteredPuertos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No se encontraron puertos con los filtros seleccionados</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredPuertos.map(renderPuertoCard)}
          </div>
        )}
      </div>
      
      {showClienteForm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {puertos.find(p => p.puerto === showClienteForm)?.cliente 
                  ? 'Editar Cliente' 
                  : 'Asignar Cliente'
                } - Puerto #{showClienteForm}
              </h3>
              <button
                onClick={() => {
                  setShowClienteForm(null)
                  setError(null)
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID Cliente *
                </label>
                <input
                  type="text"
                  value={clienteForm.clienteId}
                  onChange={(e) => setClienteForm({ ...clienteForm, clienteId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="CLI-XXXXX"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  value={clienteForm.nombreCliente}
                  onChange={(e) => setClienteForm({ ...clienteForm, nombreCliente: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Nombre completo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ONT ID / MAC
                </label>
                <input
                  type="text"
                  value={clienteForm.ontId}
                  onChange={(e) => setClienteForm({ ...clienteForm, ontId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="HWTC-XXXXXXXX"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modelo ONT
                </label>
                <input
                  type="text"
                  value={clienteForm.ontModelo}
                  onChange={(e) => setClienteForm({ ...clienteForm, ontModelo: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="HG8546M, etc."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paquete de Servicio
                </label>
                <select
                  value={clienteForm.paqueteServicio}
                  onChange={(e) => setClienteForm({ ...clienteForm, paqueteServicio: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Seleccionar paquete...</option>
                  {PAQUETES_SERVICIO.map(paquete => (
                    <option key={paquete} value={paquete}>{paquete}</option>
                  ))}
                </select>
              </div>
              
              {splitters.length > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <label className="block text-sm font-medium text-purple-700 mb-2">
                    Conectar a Splitter
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="px-2 py-1.5 border rounded text-sm"
                      onChange={(e) => {
                        const [splitterId, puerto] = e.target.value.split('|')
                        if (splitterId && puerto && showClienteForm) {
                          handleConectarASplitter(showClienteForm, splitterId, parseInt(puerto))
                        }
                      }}
                    >
                      <option value="">Seleccionar salida...</option>
                      {splitters.flatMap(sp => 
                        sp.salidas
                          .filter(s => s.estado === 'libre')
                          .map(s => (
                            <option key={`${sp.id}|${s.numero}`} value={`${sp.id}|${s.numero}`}>
                              {sp.id} → Puerto {s.numero}
                            </option>
                          ))
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowClienteForm(null)
                  setError(null)
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleAsignarCliente(showClienteForm)}
                className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4" />
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PuertosNAPConfig
